import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  FormControl,
  Select,
  MenuItem,
  Dialog as ZoomDialog,
  useTheme,
  useMediaQuery,
  IconButton,
} from '@mui/material';
import { toast } from 'react-toastify';
import CloseIcon from '@mui/icons-material/Close';

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
};

const formatTime = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
};

// Helper to fix invalid URLs like "http:/..." → "http://..."
const fixImageUrl = (url) => {
  if (!url) return null;
  return url.replace(/^http:\/([^/])/, 'http://$1');
};

const TaskAssDialog = ({
  open,
  onClose,
  ticket,
  providers,
  onAssign,
  getStatusChip,
  setSelectedRows,
  setSelectedCategoryId,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [selectedProvider, setSelectedProvider] = useState(ticket?.ServiceProvId || '');
  const [zoomOpen, setZoomOpen] = useState(false);

  if (!ticket || !open) return null;

  const imageUrl = fixImageUrl(ticket.ImgName);
  const hasImage = !!imageUrl;

  const handleAssign = () => {
    if (!providers || providers.length === 0) {
      toast.error('No providers available to assign.');
      return;
    }
    if (!selectedProvider) {
      toast.warning('Please select a provider to assign.');
      return;
    }
    onAssign(ticket.TicketId, selectedProvider);
    onClose();
  };

  const handleCancel = () => {
    if (typeof setSelectedRows === 'function') {
      setSelectedRows([]);
    }
    if (typeof setSelectedCategoryId === 'function') {
      setSelectedCategoryId(null);
    }
    onClose();
  };

  const LabelValue = ({ label, value }) => (
    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
      <Typography fontWeight="bold" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
        {label}:
      </Typography>
      <Typography>{value || 'N/A'}</Typography>
    </Box>
  );

  return (
    <>
      {/* Main Dialog */}
      <Dialog
        open={open}
        onClose={handleCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: 'hidden',
            boxShadow: 6,
          },
        }}
      >
        <DialogTitle
          sx={{
            pt: 1,
            pb: 1,
            px: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontWeight: 'bold',
            fontSize: { xs: '1.2rem', sm: '1.6rem' },
            background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            color: '#fff',
          }}
        >
          <Typography
            variant="h6"
            component="div"
            sx={{
              fontWeight: 'bold',
              fontSize: { xs: '1.1rem', sm: '1.4rem' },
              color: '#fff',
            }}
          >
            Task Assignment
          </Typography>

          <Box sx={{ ml: 2 }}>
            {getStatusChip(ticket.TicketSt)}
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
          <Grid container spacing={2}>
            {/* Info Section */}
            <Grid item xs={12} sm={8}>
              <Box mb={1}><LabelValue label="Property" value={ticket.PropName} /></Box>
              <Box mb={1}><LabelValue label="Ticket No" value={ticket.TicketNo} /></Box>
              <Box mb={1}><LabelValue label="Complain" value={ticket.ServiceName} /></Box>

              <Grid container mb={1} alignItems="center" justifyContent="flex-start" spacing={0}>
                <Grid item>
                  <LabelValue label="Date" value={formatDate(ticket.TktDate)} />
                </Grid>
                {!isMobile && (
                  <>
                    <Grid item>
                      <Box sx={{ mx: 2 }}>|</Box>
                    </Grid>
                    <Grid item>
                      <LabelValue label="Time" value={formatTime(ticket.TktDate)} />
                    </Grid>
                  </>
                )}
              </Grid>

              {/* Tkt For / Priority / Tag row */}
              <Grid
                container
                mb={1}
                alignItems="center"
                spacing={1}
                sx={{ flexWrap: isMobile ? 'wrap' : 'nowrap' }}
              >
                <Grid item xs="auto" sx={{ display: 'flex', alignItems: 'center' }}>
                  <LabelValue label="Tkt For" value={ticket.TktFor || 'N/A'} />
                  {!isMobile && (
                    <Typography sx={{ mx: 1, userSelect: 'none', color: 'text.secondary' }}>|</Typography>
                  )}
                </Grid>
                <Grid item xs="auto" sx={{ display: 'flex', alignItems: 'center' }}>
                  <LabelValue label="Priority" value={ticket.TicketServName || 'N/A'} />
                  {!isMobile && (
                    <Typography sx={{ mx: 1, userSelect: 'none', color: 'text.secondary' }}>|</Typography>
                  )}
                </Grid>
                <Grid item xs="auto" sx={{ display: 'flex', alignItems: 'center' }}>
                  <LabelValue label="Tag" value={ticket.TicketTagName || 'N/A'} />
                </Grid>
              </Grid>

              {/* Assign To Dropdown */}
              <Grid container spacing={1} alignItems="center" mt={0}>
                <Grid item xs="auto">
                  <Typography fontWeight="bold" color="text.secondary" sx={{ fontSize: { xs: '0.99rem', sm: '1rem' } }}>
                    Assign To:
                  </Typography>
                </Grid>
                <Grid item xs>
                  <FormControl size="small" sx={{ width: '100%' }}>
                    <Select
                      value={selectedProvider}
                      onChange={(e) => setSelectedProvider(e.target.value)}
                      displayEmpty
                      renderValue={(selected) => {
                        if (!selected) return <em>Select Provider</em>;
                        const prov = providers.find(p => p.ServiceProvId === selected);
                        return prov?.ServiceProvName || '';
                      }}
                    >
                      {providers.map((prov) => (
                        <MenuItem key={prov.ServiceProvId} value={prov.ServiceProvId}>
                          {prov.ServiceProvName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>

            {/* Image Section  */}
            <Grid item xs={12} sm={4} sx={{ textAlign: 'center' }}>
              <Box
                sx={{
                  width: { xs: 100, sm: 120 },
                  height: { xs: 100, sm: 120 },
                  borderRadius: 2,
                  overflow: 'hidden',
                  position: 'relative',
                  bgcolor: hasImage ? 'transparent' : 'grey.50',
                  border: hasImage
                    ? `2px solid ${theme.palette.primary.main}`
                    : `2px dashed ${theme.palette.grey[400]}`,
                  boxShadow: hasImage ? 2 : 1,
                  cursor: hasImage ? 'pointer' : 'default',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                }}
                onClick={() => hasImage && setZoomOpen(true)}
              >
                {hasImage ? (
                  <Box
                    component="img"
                    src={imageUrl}
                    alt="Ticket"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.7rem', fontWeight: 500 }}
                  >
                    No Image
                  </Typography>
                )}
              </Box>

              <Typography
                variant="caption"
                display="block"
                mt={1}
                color="text.secondary"
                sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}
              >
                {hasImage ? 'Click to Zoom' : ''}
              </Typography>
            </Grid>


          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: 2, pt: 1, justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAssign}
            disabled={!selectedProvider}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 'bold',
              px: 4,
              fontSize: { xs: '0.99rem', sm: '1rem' },
            }}
          >
            Assign
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={handleCancel}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 'bold',
              px: 4,
              fontSize: { xs: '0.99rem', sm: '1rem' },
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Zoom Dialog  */}
      <ZoomDialog
        open={zoomOpen}
        onClose={() => setZoomOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { backgroundColor: 'transparent', boxShadow: 'none', m: 2 },
        }}
        BackdropProps={{ sx: { backgroundColor: 'rgba(0,0,0,0.95)' } }}
      >
        <IconButton
          onClick={() => setZoomOpen(false)}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            bgcolor: 'rgba(255,255,255,0.9)',
            color: 'black',
            '&:hover': { bgcolor: 'white' },
            zIndex: 10,
            boxShadow: 3,
          }}
        >
          <CloseIcon />
        </IconButton>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '90vh',
            cursor: 'zoom-out',
          }}
          onClick={() => setZoomOpen(false)}
        >
          {hasImage ? (
            <Box
              component="img"
              src={imageUrl}
              alt="Zoomed Ticket"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
              sx={{
                maxWidth: '90%',
                maxHeight: '90%',
                objectFit: 'contain',
                borderRadius: 2,
                boxShadow: 10,
                userSelect: 'none',
              }}
            />
          ) : (
            <Typography color="white" variant="h6">
              Image Not Available
            </Typography>
          )}
        </Box>
      </ZoomDialog>
    </>
  );
};

export default TaskAssDialog;